/**
 * Service Lines Data
 * Organized by category with color coding
 */

export interface ServiceLine {
  name: string;
  skillsets: string[];
  leadershipRoles: string[];
  contributorRoles: string[];
}

export interface ServiceCategory {
  id: string;
  name: string;
  color: string; // Tailwind color class base (e.g., 'blue', 'green')
  icon: string; // Icon name or emoji
  serviceLines: ServiceLine[];
}

export const serviceCategories: ServiceCategory[] = [
  // ============================================
  // CATEGORY 1: Enterprise Applications
  // ============================================
  {
    id: 'enterprise-apps',
    name: 'Enterprise Applications',
    color: 'purple',
    icon: '🏢',
    serviceLines: [
      {
        name: 'ERP & Business Applications',
        skillsets: [
          'SAP',
          'Oracle',
          'Microsoft Dynamics',
          'JD Edwards',
          'Deltek Vantagepoint',
          'NetSuite',
        ],
        leadershipRoles: [
          'Interim Supply Chain Systems Director',
          'ERP Program Director',
          'Program/Project Managers',
          'Business Applications Director',
        ],
        contributorRoles: [
          'Business Analysts',
          'Functional Consultants',
          'Application Support Specialists',
          'Implementation Consultants *across all modules',
        ],
      },
      {
        name: 'CRM & Customer Experience',
        skillsets: [
          'Salesforce',
          'Microsoft Dynamics CRM',
          'HubSpot',
          'Oracle CX',
          'Kingsway',
        ],
        leadershipRoles: [
          'Interim Director Sales & Analytics Systems',
          'Customer Experience Lead',
          'CRM Solutions Architect',
        ],
        contributorRoles: [
          'CRM Developer',
          'CRM Administrator',
          'CRM Analyst',
          'Customer Data Analyst',
          'Marketing Automation Specialist',
        ],
      },
      {
        name: 'Financial Planning & Analysis',
        skillsets: [
          'Oracle Hyperion',
          'Anaplan',
          'Adaptive Insights',
          'OneStream',
          'IBM Planning Analytics',
        ],
        leadershipRoles: [
          'FP&A Solutions Architect',
          'Financial Systems Architect',
        ],
        contributorRoles: [
          'Hyperion Consultant',
          'FP&A Analyst',
          'EPM Developer',
          'Financial Systems Administrator',
          'Planning Analyst',
        ],
      },
      {
        name: 'HR Technology & Talent Management',
        skillsets: [
          'Workday',
          'SAP SuccessFactors',
          'Oracle HCM',
          'iCIMS',
          'ADP',
          'UKG',
        ],
        leadershipRoles: [
          'Interim HRIS Applications Manager',
          'Talent Systems Lead',
          'HRIS Project Manager',
        ],
        contributorRoles: [
          'HRIS Analyst',
          'HR Systems Administrator',
          'iCIMS Developer',
          'Recruiting Systems Specialist',
          'Talent Acquisition Analyst',
        ],
      },
    ],
  },

  // ============================================
  // CATEGORY 2: Cloud & Infrastructure
  // ============================================
  {
    id: 'cloud-infrastructure',
    name: 'Cloud & Infrastructure',
    color: 'blue',
    icon: '☁️',
    serviceLines: [
      {
        name: 'Cloud Infrastructure & Engineering',
        skillsets: ['AWS', 'Azure', 'Google Cloud Platform'],
        leadershipRoles: [
          'Cloud Architecture Director',
          'Interim VP of Cloud Services',
          'Senior Engineering Manager',
          'Cloud Solutions Architect',
        ],
        contributorRoles: [
          'Cloud Engineer',
          'DevOps Engineer',
          'DevSecOps Engineer',
          'Site Reliability Engineer',
          'Migration Architect',
          'Cloud Security Engineer',
        ],
      },
      {
        name: 'DevOps & Automation',
        skillsets: [
          'Jenkins',
          'GitLab',
          'Azure DevOps',
          'GitHub Actions',
          'Terraform',
          'Ansible',
          'Kubernetes',
        ],
        leadershipRoles: [
          'Interim Director Application Administration & Development',
          'Automation Platform Lead',
          'DevSecOps Architect',
        ],
        contributorRoles: [
          'DevOps Engineer',
          'Automation Engineer',
          'CI/CD Engineer',
          'Container Engineer',
          'Infrastructure as Code Specialist',
        ],
      },
      {
        name: 'Integration & Middleware',
        skillsets: [
          'Boomi',
          'MuleSoft',
          'Informatica',
          'Talend',
          'Azure Logic Apps',
          'AWS API Gateway',
        ],
        leadershipRoles: [
          'Integration Architecture Director',
          'Middleware Platform Lead',
          'Senior Integration Architect',
        ],
        contributorRoles: [
          'Integration Developer',
          'API Developer',
          'ETL Developer',
          'Middleware Engineer',
          'iPaaS Specialist',
        ],
      },
    ],
  },

  // ============================================
  // CATEGORY 3: AI, Machine Learning, & Analytics
  // ============================================
  {
    id: 'data-analytics',
    name: 'AI, Machine Learning, & Analytics',
    color: 'green',
    icon: '📊',
    serviceLines: [
      {
        name: 'Data & Analytics',
        skillsets: [
          'Snowflake',
          'Databricks',
          'Palantir',
          'Microsoft Fabric',
          'Power BI',
          'Tableau',
          'Informatica',
          'Talend',
          'Matillion',
        ],
        leadershipRoles: [
          'Business Intelligence Director',
          'Data Architecture Director',
          'Analytics Practice Lead',
          'Senior Data Architect',
        ],
        contributorRoles: [
          'Data Engineer',
          'BI Analyst',
          'ETL Developer',
          'Data Analyst',
          'Database Administrator',
          'Reporting Analyst',
        ],
      },
      {
        name: 'AI & Machine Learning',
        skillsets: [
          'OpenAI',
          'AWS SageMaker',
          'Azure AI',
          'Google Vertex AI',
          'Anthropic',
        ],
        leadershipRoles: [
          'AI Strategy Director',
          'Enterprise Intelligence Lead',
          'ML Engineering Manager',
        ],
        contributorRoles: [
          'Machine Learning Engineer',
          'AI Developer',
          'Full Stack Web AI Developer',
          'Data Scientist',
          'AI Solutions Engineer',
          'Prompt Engineer',
        ],
      },
      {
        name: 'Master Data Management',
        skillsets: [
          'Informatica MDM',
          'SAP MDM',
          'Profisee',
          'Semarchy',
          'Reltio',
        ],
        leadershipRoles: [
          'MDM Lead',
          'Data Governance Lead',
          'Enterprise Data Architect',
        ],
        contributorRoles: [
          'Master Data Analyst',
          'Data Steward',
          'MDM Administrator',
          'Data Quality Analyst',
          'Data Governance Specialist',
        ],
      },
    ],
  },

  // ============================================
  // CATEGORY 4: Supply Chain & Logistics
  // ============================================
  {
    id: 'supply-chain',
    name: 'Supply Chain & Logistics',
    color: 'orange',
    icon: '📦',
    serviceLines: [
      {
        name: 'Supply Chain Management',
        skillsets: [
          'Blue Yonder',
          'Manhattan Associates',
          'Kinaxis RapidResponse',
          'o9 Solutions',
          'SAP IBP',
          'Oracle SCM Cloud',
        ],
        leadershipRoles: [
          'Interim VP Supply Chain Solutions',
          'SCM Practice Director',
          'Supply Chain Transformation Lead',
        ],
        contributorRoles: [
          'Supply Chain Analyst',
          'Demand Planner',
          'Inventory Analyst',
          'SCM Consultant',
          'RapidResponse Developer',
          'WFM Consultant',
        ],
      },
      {
        name: 'Warehouse & Logistics',
        skillsets: [
          'Manhattan Associates (WMS)',
          'Körber',
          'Blue Yonder',
          'SAP EWM',
          'Oracle WMS',
        ],
        leadershipRoles: [
          'Director of Logistics Solutions',
          'WMS Practice Lead',
          'Supply Chain Operations Director',
        ],
        contributorRoles: [
          'WMS Consultant',
          'Implementation Specialist',
          'Logistics Analyst',
          'Warehouse Operations Analyst',
        ],
      },
      {
        name: 'Product Lifecycle Management',
        skillsets: [
          'Siemens Teamcenter',
          'PTC Windchill',
          'Dassault ENOVIA',
          'Arena PLM',
        ],
        leadershipRoles: [
          'PLM Solutions Architect',
          'Product Engineering Lead',
        ],
        contributorRoles: [
          'PLM Consultant',
          'Teamcenter Developer',
          'PLM Administrator',
          'Configuration Specialist',
          'Data Migration Specialist',
        ],
      },
    ],
  },

  // ============================================
  // CATEGORY 5: Software Engineering
  // ============================================
  {
    id: 'software-engineering',
    name: 'Software Engineering',
    color: 'teal',
    icon: '💻',
    serviceLines: [
      {
        name: 'Application Development',
        skillsets: [
          'Microsoft .NET',
          'Java/Spring',
          'React',
          'Node.js',
          'Python',
          'Full Stack Platforms',
        ],
        leadershipRoles: [
          'Application Development Director',
          'Technical Architect',
          'Engineering Manager',
          'Dev Team Lead',
        ],
        contributorRoles: [
          'Full Stack Developer',
          'Front-End Developer',
          'Back-End Developer',
          'Software Engineer',
          'UX/UI Designer',
        ],
      },
      {
        name: 'Quality Assurance & Testing',
        skillsets: [
          'Selenium',
          'TestComplete',
          'Katalon',
          'Tricentis',
          'Micro Focus',
        ],
        leadershipRoles: [
          'QA Director',
          'Test Automation Lead',
          'Quality Engineering Manager',
        ],
        contributorRoles: [
          'QA Engineer',
          'Test Automation Engineer',
          'Quality Analyst',
          'Performance Test Engineer',
          'QA Automation Architect',
        ],
      },
      {
        name: 'Digital Commerce',
        skillsets: [
          'Salesforce Commerce Cloud',
          'Adobe Commerce (Magento)',
          'Oro Commerce',
          'SAP Commerce',
          'Shopify Plus',
          'commercetools',
        ],
        leadershipRoles: [
          'Digital Commerce Strategy Lead',
          'Digital Commerce Lead',
          'Commerce Solutions Architect',
        ],
        contributorRoles: [
          'Commerce Developer',
          'Digital Merchandising Analyst',
          'Commerce Administrator',
          'Commerce Business Analyst',
        ],
      },
      {
        name: 'Enterprise Collaboration',
        skillsets: [
          'Microsoft 365/SharePoint',
          'Atlassian (Jira/Confluence)',
          'Slack',
          'Teams',
        ],
        leadershipRoles: [
          'Collaboration Solutions Director',
          'Digital Workplace Lead',
          'SharePoint Architect',
        ],
        contributorRoles: [
          'SharePoint Developer',
          'Collaboration Analyst',
          'Jira Administrator',
          'Migration Specialist',
          'Knowledge Management Specialist',
        ],
      },
    ],
  },

  // ============================================
  // CATEGORY 6: Business Enablement
  // ============================================
  {
    id: 'business-enablement',
    name: 'Business Enablement',
    color: 'slate',
    icon: '📈',
    serviceLines: [
      {
        name: 'Project & Portfolio Management',
        skillsets: [
          'Microsoft Project',
          'Smartsheet',
          'Jira',
          'ServiceNow PPM',
          'Planview',
          'Clarity PPM',
        ],
        leadershipRoles: [
          'PMO Director',
          'Program Director',
          'Portfolio Manager',
          'Senior Project Manager',
        ],
        contributorRoles: [
          'Project Manager',
          'Project Coordinator',
          'PMO Analyst',
          'Scheduler',
          'Resource Manager',
        ],
      },
      {
        name: 'Change Management & Organizational Development',
        skillsets: ['Prosci', 'ADKAR', 'ServiceNow', 'Microsoft Viva'],
        leadershipRoles: [
          'Interim Change Management Director',
          'Organizational Change Lead',
          'Interim Transformation Director',
        ],
        contributorRoles: [
          'Change Management Consultant',
          'Change Analyst',
          'Training Specialist',
          'Communications Specialist',
          'Adoption Manager',
        ],
      },
      {
        name: 'Digital Adoption & Training',
        skillsets: ['WalkMe', 'Whatfix', 'Pendo', 'Appcues', 'UserGuiding'],
        leadershipRoles: [
          'User Experience Lead',
          'Training & Enablement Manager',
        ],
        contributorRoles: [
          'Digital Adoption Consultant',
          'User Experience Specialist',
          'Training Developer',
          'Adoption Analyst',
        ],
      },
    ],
  },
];
